import React, { FunctionComponent, useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { isEmpty } from 'lodash-es'
import { AdvancedFieldsWrapper, DataGridFiltersWrapper, FieldsWrapper, FiltersSlideDown, ToolbarToggleWrapper } from './filtersStyles'
import { NumericInputField, TextInputField } from 'view/components/inputs'
import {
  getTextFlagButtonState,
  TextButton,
  TextButtonType,
  TextFlagButtonsContainer,
  ToolbarTextFlagButton
} from 'view/components/buttons'
import {
  AuthorisedUser,
  Collection,
  DealSubscriptionIdentifier,
  EntityTypeEnum,
  FilterOperation,
  RepoType,
  SearchDealStatus,
  SettlementVenue,
  SubscriptionFilter,
  UserPreference,
  ValidationIssue
} from 'api'
import { useChangedSubscriptionFilters } from 'view/shared/entity/hooks/useChangedSubscriptionFilters'
import { createEntitySubscriptionFiltersSelector } from 'selectors/selectionSelectors'
import { authorisedUserSelector } from 'selectors/connectionSelectors'
import { areOnlyDefaultSubscriptionFiltersActive, RepoTypeHelper } from 'model'
import {
  currentAndActiveSubscriptionFiltersAreEqual,
  getCollectionNameFromId,
  getDateRangeSubscriptionFilters,
  getDateRangeSubscriptionFilterValue,
  getSubscriptionFilterValue,
  getUpdatedSubscriptionFilters,
  mapDealStatusToSearchDealStatus,
  mapSearchDealStatusToDealStatus,
  SubscriptionFiltersProps
} from './utils'
import { Locale, Translation } from 'locale'
import { SelectboxField } from 'view/components/options'
import { createAllEntitiesSortedSelector } from 'selectors/entitySelectors'
import { CurrencySelectboxField, TermSelectboxField } from 'view/shared/entity'
import { userPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { getRatePercentFormat } from 'view/shared/datagrid'
import { isAdminGatewayUserSelector } from 'selectors/userSelectors'
import { isBuySideTraderSelector } from 'selectors/permissionSelectors'
import { ReversedDirectionSelectboxField } from 'view/shared/entity/ReversedDirectionSelectbox'
import { isNotEmpty } from 'utils/predicate'
import { ProductSubscriptionSelectbox } from './SubscriptionFilterComponents/ProductSubscriptionSelectbox'
import { ProductSectorSubscriptionSelectbox } from './SubscriptionFilterComponents/ProductSectorSubscriptionSelectbox'
import { SubscriptionDateRangeComponent, SubscriptionDates } from './SubscriptionFilterComponents/SubscriptionDateRange'
import { MyFirmSubscriptionCombobox } from './SubscriptionFilterComponents/MyFirmSubscriptionCombobox'
import { MyEntitySubscriptionCombobox } from './SubscriptionFilterComponents/MyEntitySubscriptionCombobox'
import { CounterpartySubscriptionCombobox } from './SubscriptionFilterComponents/CounterpartySubscriptionCombobox'
import { CounterpartyFirmSubscriptionCombobox } from './SubscriptionFilterComponents/CounterpartyFirmSubscriptionCombobox'
import { MyTraderSubscriptionCombobox } from './SubscriptionFilterComponents/MyTraderSubscriptionCombobox'
import { CounterpartyTraderSubscriptionCombobox } from './SubscriptionFilterComponents/CounterpartyTraderSubscriptionCombobox'
import { IsinCusipSubscriptionField } from './SubscriptionFilterComponents/IsinCusipSubscriptionField'
import { ToolbarTextFlag } from 'view/components/buttons/ToolbarTextFlag'

type AdvancedDealSubscriptionFiltersProps = StateProps & OwnProps

interface StateProps {
  activeSubscriptionFilters: SubscriptionFiltersProps['activeSubscriptionFilters']
  authorisedUser: AuthorisedUser
  allCollections: Collection[]
  userPreference: UserPreference
  isAdmin: boolean
  isBuySideUser: boolean
}

interface OwnProps {
  subscriptionIdentifier: DealSubscriptionIdentifier
}

const subscriptionIdentifierSelector = (_: {}, props: AdvancedDealSubscriptionFiltersProps) => props.subscriptionIdentifier

const mapStateToProps = createStructuredSelector({
  activeSubscriptionFilters: createEntitySubscriptionFiltersSelector(() => EntityTypeEnum.DEAL, subscriptionIdentifierSelector),
  authorisedUser: authorisedUserSelector,
  allCollections: createAllEntitiesSortedSelector(EntityTypeEnum.COLLECTION),
  userPreference: userPreferenceSelector,
  isAdmin: isAdminGatewayUserSelector,
  isBuySideUser: isBuySideTraderSelector
})

const AdvancedDealSubscriptionFiltersFieldNames = [
  'axeId',
  'tradeId',
  'tradedAt',
  'startDate',
  'endDate',
  'collateralId',
  'fundId',
  'initiatorOrganisationId',
  'counterpartyId',
  'rate',
  'currency',
  'term',
  'direction',
  'productSectorId',
  'traderUserId',
  'counterpartyTraderId',
  'collectionId',
  'negotiationId',
  'orderId',
  'status',
  'settlementVenue',
  'repoType'
]

export const AdvancedDealSubscriptionFiltersComponent: FunctionComponent<AdvancedDealSubscriptionFiltersProps> = ({
  subscriptionIdentifier,
  activeSubscriptionFilters,
  authorisedUser,
  allCollections,
  userPreference,
  isAdmin,
  isBuySideUser
}) => {
  const onChangeSubscription = useChangedSubscriptionFilters(EntityTypeEnum.DEAL, subscriptionIdentifier, true)
  const isTodayFilterActive = areOnlyDefaultSubscriptionFiltersActive(subscriptionIdentifier, activeSubscriptionFilters, authorisedUser)
  const activeAdvancedDealSubscriptionFilters = activeSubscriptionFilters.filter(activeSubscriptionFilter =>
    AdvancedDealSubscriptionFiltersFieldNames.includes(activeSubscriptionFilter.fieldName)
  )

  const [subscriptionFilters, setSubscriptionFilters] = useState<SubscriptionFilter[]>(
    isTodayFilterActive ? [] : activeAdvancedDealSubscriptionFilters
  )
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([])

  const [searchBarCollapsed, setSearchBarCollapsed] = useState<boolean>(isTodayFilterActive)

  const searchButtonDisabled =
    currentAndActiveSubscriptionFiltersAreEqual(subscriptionFilters, activeAdvancedDealSubscriptionFilters) ||
    isNotEmpty(validationIssues) ||
    isEmpty(subscriptionFilters)

  const handleSubscribeToday = () => {
    onChangeSubscription(null)
    setSearchBarCollapsed(true)
  }

  const handleSubscribeHistorical = () => {
    onChangeSubscription(subscriptionFilters)
  }

  const handleUpdateFilterValue = (fieldName: string, filterOperation: FilterOperation, orFilter?: SubscriptionFilter) => (value: any) => {
    const updatedSubscriptionFilters = getUpdatedSubscriptionFilters(subscriptionFilters, fieldName, value, filterOperation, orFilter)
    setSubscriptionFilters(updatedSubscriptionFilters)
  }

  const handleDateRangeUpdate = (fieldName: string, isDateAndTimeFilter = false) => (
    subscriptionDates: SubscriptionDates,
    validationIssues: ValidationIssue[]
  ) => {
    setValidationIssues(validationIssues)
    setSubscriptionFilters(getDateRangeSubscriptionFilters(subscriptionDates, fieldName, subscriptionFilters, isDateAndTimeFilter))
  }

  return (
    <DataGridFiltersWrapper>
      <ToolbarToggleWrapper>
        <div>{!isTodayFilterActive && Locale.translate(Translation.SC_FILTER_SEARCH_BY)}</div>
        <TextFlagButtonsContainer>
          <ToolbarTextFlagButton
            dataTestId={'AdvancedDealSubscriptionFilters-ToolbarTextFlagButton-ButtonToday'}
            state={getTextFlagButtonState(isTodayFilterActive)}
            onClick={handleSubscribeToday}>
            {Locale.translate(Translation.TC_FILTER_TODAYS)}
          </ToolbarTextFlagButton>

          <ToolbarTextFlag
            dataTestId={'AdvancedDealSubscriptionFilters-ToolbarTextFlagButton-SearchButton'}
            collapsed={!searchBarCollapsed}
            state={getTextFlagButtonState(!isTodayFilterActive)}
            onClick={() => {
              if (isTodayFilterActive) onChangeSubscription([])
              setSearchBarCollapsed(!searchBarCollapsed)
            }}
            text={Locale.translate(Translation.TC_FILTER_SEARCH)}
          />
        </TextFlagButtonsContainer>
      </ToolbarToggleWrapper>
      <FiltersSlideDown closed={searchBarCollapsed}>
        <div>
          <AdvancedFieldsWrapper>
            <MyFirmSubscriptionCombobox subscriptionFilters={subscriptionFilters} handleUpdateFilterValue={handleUpdateFilterValue} />
            <MyEntitySubscriptionCombobox subscriptionFilters={subscriptionFilters} handleUpdateFilterValue={handleUpdateFilterValue} />
            <CounterpartyFirmSubscriptionCombobox
              subscriptionFilters={subscriptionFilters}
              handleUpdateFilterValue={handleUpdateFilterValue}
            />
            <CounterpartySubscriptionCombobox subscriptionFilters={subscriptionFilters} handleUpdateFilterValue={handleUpdateFilterValue} />
          </AdvancedFieldsWrapper>
          <AdvancedFieldsWrapper>
            <MyTraderSubscriptionCombobox subscriptionFilters={subscriptionFilters} handleUpdateFilterValue={handleUpdateFilterValue} />
            <CounterpartyTraderSubscriptionCombobox
              subscriptionFilters={subscriptionFilters}
              handleUpdateFilterValue={handleUpdateFilterValue}
            />
            <SelectboxField
              label={Locale.translate(Translation.TC_SETTLEMENT_TYPE)}
              dataTestId={'AdvancedDealSubscriptionFilters-TextInput-SettlementTypeEquals'}
              optionKeys={Object.values(SettlementVenue)}
              value={getSubscriptionFilterValue(subscriptionFilters, 'settlementVenue')}
              onUpdate={handleUpdateFilterValue('settlementVenue', FilterOperation.EQUALS)}
              canClear={true}
            />
            <TextInputField
              label={Locale.translate(Translation.SC_FILTER_COLLECTION_NAME_EQUALS)}
              dataTestId={'AdvancedDealSubscriptionFilters-TextInput-CollectionName'}
              value={getCollectionNameFromId(allCollections, getSubscriptionFilterValue(subscriptionFilters, 'collectionId'))}
              onUpdate={handleUpdateFilterValue('collectionId', FilterOperation.EQUALS)}
            />
          </AdvancedFieldsWrapper>
          <AdvancedFieldsWrapper>
            <NumericInputField
              label={Locale.translate(Translation.SC_FILTER_RATE_FROM)}
              dataTestId={'AdvancedDealSubscriptionFilters-NumericInput-RateFrom'}
              formatOptions={getRatePercentFormat()}
              value={getSubscriptionFilterValue(subscriptionFilters, 'rate', FilterOperation.AFTER)}
              onUpdate={(value: number | null | undefined) => handleUpdateFilterValue('rate', FilterOperation.AFTER)(value?.toString())}
            />
            <NumericInputField
              label={Locale.translate(Translation.SC_FILTER_RATE_TO)}
              dataTestId={'AdvancedDealSubscriptionFilters-NumericInput-RateTo'}
              formatOptions={getRatePercentFormat()}
              value={getSubscriptionFilterValue(subscriptionFilters, 'rate', FilterOperation.BEFORE_OR_EQUAL)}
              onUpdate={(value: number | null | undefined) =>
                handleUpdateFilterValue('rate', FilterOperation.BEFORE_OR_EQUAL)(value?.toString())
              }
            />
            <ProductSubscriptionSelectbox subscriptionFilters={subscriptionFilters} handleUpdateFilterValue={handleUpdateFilterValue} />
            <ProductSectorSubscriptionSelectbox
              subscriptionFilters={subscriptionFilters}
              handleUpdateFilterValue={handleUpdateFilterValue}
            />
          </AdvancedFieldsWrapper>
          <AdvancedFieldsWrapper>
            <TermSelectboxField
              dataTestId={'AdvancedDealSubscriptionFilters-TermSelectbox-Term'}
              label={Locale.translate(Translation.SC_FILTER_TERM_EQUALS)}
              canClear={true}
              value={getSubscriptionFilterValue(subscriptionFilters, 'term')}
              onUpdate={handleUpdateFilterValue('term', FilterOperation.EQUALS)}
            />
            <ReversedDirectionSelectboxField
              label={Locale.translate(Translation.SC_FILTER_DIRECTION_EQUALS)}
              dataTestId={'AdvancedDealSubscriptionFilters-DirectionSelectbox-Direction'}
              canClear={true}
              directionFormat={userPreference.directionFormat}
              value={getSubscriptionFilterValue(subscriptionFilters, 'direction')}
              onUpdate={handleUpdateFilterValue('direction', FilterOperation.EQUALS)}
              reversed={!isBuySideUser && !isAdmin}
            />
            <CurrencySelectboxField
              label={Locale.translate(Translation.SC_FILTER_CURRENCY_EQUALS)}
              dataTestId={'AdvancedDealSubscriptionFilters-CurrencySelectbox-Currency'}
              canClear={true}
              value={getSubscriptionFilterValue(subscriptionFilters, 'currency')}
              onUpdate={handleUpdateFilterValue('currency', FilterOperation.EQUALS)}
            />
            <IsinCusipSubscriptionField subscriptionFilters={subscriptionFilters} handleUpdateFilterValue={handleUpdateFilterValue} />
          </AdvancedFieldsWrapper>
          <AdvancedFieldsWrapper>
            <TextInputField
              label={Locale.translate(Translation.TC_RFQ_ID)}
              dataTestId={'AdvancedDealSubscriptionFilters-TextInput-RfqIdEquals'}
              value={getSubscriptionFilterValue(subscriptionFilters, 'orderId')}
              onUpdate={handleUpdateFilterValue('orderId', FilterOperation.EQUALS)}
              autoCapitalise={true}
            />
            <TextInputField
              label={Locale.translate(Translation.TC_NEGOTIATION_ID)}
              dataTestId={'AdvancedDealSubscriptionFilters-TextInput-NegotiationIdEquals'}
              value={getSubscriptionFilterValue(subscriptionFilters, 'negotiationId')}
              onUpdate={handleUpdateFilterValue('negotiationId', FilterOperation.EQUALS)}
              autoCapitalise={true}
            />
            <TextInputField
              dataTestId={'AdvancedDealSubscriptionFilters-TextInput-TradeIdEquals'}
              label={Locale.translate(Translation.SC_FILTER_TRADE_ID_EQUALS)}
              value={getSubscriptionFilterValue(subscriptionFilters, 'tradeId')}
              onUpdate={handleUpdateFilterValue('tradeId', FilterOperation.EQUALS)}
              autoCapitalise={true}
            />
            <TextInputField
              dataTestId={'AdvancedDealSubscriptionFilters-TextInput-AxeIdEquals'}
              label={Locale.translate(Translation.TC_AXE_ID)}
              value={getSubscriptionFilterValue(subscriptionFilters, 'axeId')}
              onUpdate={handleUpdateFilterValue('axeId', FilterOperation.EQUALS)}
              autoCapitalise={true}
            />
          </AdvancedFieldsWrapper>
          <AdvancedFieldsWrapper>
            <SelectboxField
              label={Locale.translate(Translation.TC_STATUS)}
              dataTestId={'AdvancedDealSubscriptionFilters-TextInput-StatusEquals'}
              optionKeys={Object.values(SearchDealStatus)}
              value={mapDealStatusToSearchDealStatus(getSubscriptionFilterValue(subscriptionFilters, 'status'))}
              onUpdate={(value: SearchDealStatus | null) =>
                handleUpdateFilterValue('status', FilterOperation.CONTAINS)(value ? mapSearchDealStatusToDealStatus(value) : null)
              }
              canClear={true}
            />
            <SelectboxField
              label={Locale.translate(Translation.TC_REPO_TYPE)}
              dataTestId={'AdvancedDealSubscriptionFilters-TextInput-RepoEquals'}
              getOptionText={RepoTypeHelper.getName}
              optionKeys={Object.values(RepoType)}
              value={getSubscriptionFilterValue(subscriptionFilters, 'repoType')}
              onUpdate={handleUpdateFilterValue('repoType', FilterOperation.CONTAINS)}
              canClear={true}
            />
          </AdvancedFieldsWrapper>
          <FieldsWrapper>
            <SubscriptionDateRangeComponent
              label={Translation.TC_FILTER_TRADE_DATE}
              onUpdate={handleDateRangeUpdate('tradedAt', true)}
              initialSubscriptionDates={getDateRangeSubscriptionFilterValue(subscriptionFilters, 'tradedAt')}
            />
          </FieldsWrapper>
          <FieldsWrapper>
            <SubscriptionDateRangeComponent
              label={Translation.TC_START_DATE}
              onUpdate={handleDateRangeUpdate('startDate')}
              initialSubscriptionDates={getDateRangeSubscriptionFilterValue(subscriptionFilters, 'startDate')}
            />
          </FieldsWrapper>
          <FieldsWrapper>
            <SubscriptionDateRangeComponent
              label={Translation.TC_END_DATE}
              onUpdate={handleDateRangeUpdate('endDate')}
              initialSubscriptionDates={getDateRangeSubscriptionFilterValue(subscriptionFilters, 'endDate')}
            />
          </FieldsWrapper>
        </div>
        <TextButton
          dataTestId={'AdvancedDealSubscriptionFilters-TextInput-ButtonSearch'}
          type={TextButtonType.Primary}
          disabled={searchButtonDisabled}
          onClick={handleSubscribeHistorical}>
          {Locale.translate(Translation.TC_BUTTON_SEARCH)}
        </TextButton>
      </FiltersSlideDown>
    </DataGridFiltersWrapper>
  )
}

export const AdvancedDealSubscriptionFilters = connect<StateProps, null, OwnProps>(mapStateToProps)(
  AdvancedDealSubscriptionFiltersComponent
)
